const LAST_QUERY_PARAM_KEYS = ["productType", "productSubtype", "productSubType"];

export type BrowserQueryParams = URLSearchParams | Record<string, string>;

export function getBrowserQueryString(params: BrowserQueryParams): string {
    return orderQueryEntries(getQueryEntries(params))
        .map(([key, value]) => formatQueryEntry(key, value))
        .join("&");
}

export function replaceBrowserUrlState(nextUrl: string): void {
    if (typeof window === "undefined") {
        return;
    }

    window.history.replaceState(window.history.state, "", prepareBrowserUrl(nextUrl));
}

export function replaceBrowserQueryParams(nextParams: Record<string, string>): void {
    if (typeof window === "undefined") {
        return;
    }

    const params = new URLSearchParams(window.location.search);

    Object.entries(nextParams).forEach(([key, value]) => {
        if (value) {
            params.set(key, value);
        } else {
            params.delete(key);
        }
    });

    const queryString = getBrowserQueryString(params);
    const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    const currentUrl = `${window.location.pathname}${window.location.search}`;

    if (nextUrl !== currentUrl) {
        replaceBrowserUrlState(nextUrl);
    }
}

function getQueryEntries(params: BrowserQueryParams): Array<[string, string]> {
    if (params instanceof URLSearchParams) {
        const entries: Array<[string, string]> = [];
        params.forEach((value, key) => entries.push([key, value]));
        return entries;
    }

    return Object.entries(params);
}

function orderQueryEntries(entries: Array<[string, string]>): Array<[string, string]> {
    return [
        ...entries.filter(([key]) => !LAST_QUERY_PARAM_KEYS.includes(key)),
        ...LAST_QUERY_PARAM_KEYS.flatMap((lastKey) => entries.filter(([key]) => key === lastKey)),
    ];
}

function formatQueryEntry(key: string, value: string): string {
    if (key === "filters") {
        return `${key}=${value.replace(/ /g, "+").replace(/&/g, "%26")}`;
    }

    return `${key}=${encodeURIComponent(value)}`;
}

function prepareBrowserUrl(url: string): string {
    if (!url.startsWith("/")) {
        return url;
    }

    const basePath = getBasePath();

    if (!basePath || url === basePath || url.startsWith(`${basePath}/`)) {
        return url;
    }

    return `${basePath}${url}`;
}

function getBasePath(): string {
    if (typeof document === "undefined") {
        return "";
    }

    const baseHref = document.querySelector("base")?.getAttribute("href") || "";
    const basePath = new URL(baseHref, window.location.origin).pathname.replace(/\/$/, "");

    return basePath === "" || basePath === "/" ? "" : basePath;
}
